import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { formatReportDate } from '../utils/dateUtils';
import { pdf } from '@react-pdf/renderer';
import FabricInspectionReportPDF from '../components/FabricInspectionReportPDF';
import MigrosFabricInspectionReportPDF from '../components/MigrosFabricInspectionReportPDF';
import { useAllowedReportTemplates } from '../utils/reportTemplates';

type PdfTemplate = 'qa-mate' | 'migros';

interface FabricReportData {
  id: string;
  result: string;
  total_penalty_points: number;
  total_defects: number;
  rolls_inspected: number;
  created_at: string;
  inspector_name: string;
  supplier: string;
  buyer_name: string;
  po_number: string;
  style_number: string;
  fabric_type: string;
  color_shade: string;
  total_yards: number;
  composition: any;
  pantone: any;
  target_gsm: number;
  selvedge: number;
  length: number;
  width: number;
  target_epi: number;
  original_epi: number;
  target_ppi: number;
  original_ppi: number;
  inspection_standard: string;
  aql_points: number;
  lot_size: number;
  sample_size: number;
  overall_remarks: string;
  recommendation: string;
  qa_signature_url: string;
  factory_signature_url: string;
  inspection_basis?: string;
  checklists: Array<{
    title: string;
    status: string;
    remarks: string;
    evidence_urls: string[];
  }>;
  defects: Array<{
    roll_number: number;
    points: number;
    type: string;
    category?: string;
    defect_type?: string;
    description: string;
    yard_number: string;
    image_urls: string[];
  }>;
  photos: Array<{
    id: string;
    label: string;
    image_url: string;
    is_required: boolean;
  }>;
  gsm_samples?: Array<{
    sample_size?: number;
    original_gsm?: number;
    sampleSize?: number;
    originalGsm?: number;
  }>;
  gsmSamples?: Array<any>;
  roll_width_samples?: Array<{
    length_point?: number | null;
    value?: number | null;
    unit?: string;
  }>;
  bow_skew_samples?: Array<{
    length_point?: number | null;
    value?: number | null;
    unit?: string;
  }>;
  roll_width_images?: string[];
  bow_skew_images?: string[];
}

const FabricInspectionReportDetail: React.FC = () => {
  const { id } = useParams();
  const { token } = useAuth();
  const [report, setReport] = useState<FabricReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const zoomContainerRef = useRef<HTMLDivElement>(null);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [fabricTypes, setFabricTypes] = useState<any[]>([]);

  // ── PDF generation state ──
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [pdfStatusIndex, setPdfStatusIndex] = useState(0);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [downloadingImages, setDownloadingImages] = useState(false);
  const [imageStatusIndex, setImageStatusIndex] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState<PdfTemplate>('qa-mate');
  const [isTemplateDropdownOpen, setIsTemplateDropdownOpen] = useState(false);
  const templateDropdownRef = useRef<HTMLDivElement>(null);

  const templateOptions: { value: PdfTemplate; name: string; icon: string; iconBg: string; iconColor: string; description: string }[] = [
    {
      value: 'qa-mate',
      name: 'QA Mate Template',
      icon: 'verified',
      iconBg: 'bg-blue-100',
      iconColor: 'text-[#136bce]',
      description: 'Default QA Mate fabric inspection report layout.',
    },
    {
      value: 'migros',
      name: 'Migros Template',
      icon: 'inventory_2',
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      description: 'Migros Asia — Final Random Inspection (Textile/Hometextile) Version-1 layout, adapted to fabric inspection data.',
    },
  ];
  const allowedTemplates = useAllowedReportTemplates();
  const visibleTemplateOptions = templateOptions.filter(o => allowedTemplates.includes(o.value));
  const selectedTemplateOption =
    visibleTemplateOptions.find(t => t.value === selectedTemplate) ??
    visibleTemplateOptions[0] ??
    templateOptions[0];
  useEffect(() => {
    if (visibleTemplateOptions.length && !visibleTemplateOptions.some(o => o.value === selectedTemplate)) {
      setSelectedTemplate(visibleTemplateOptions[0].value);
    }
  }, [visibleTemplateOptions, selectedTemplate]);

  const pdfStatusMessages = [
    'Preparing inspection data…',
    'Securing report metadata…',
    'Fetching fabric evidence images…',
    'Compiling defect log…',
    'Embedding signatures…',
    'Rendering high-quality pages…',
    'Almost there — finalizing your PDF…',
  ];

  const imageStatusMessages = [
    'Collecting image list…',
    'Fetching documentation photos…',
    'Fetching checklist evidence…',
    'Fetching defect images…',
    'Fetching dimension images…',
    'Compressing into ZIP archive…',
    'Almost done — finalizing your download…',
  ];

  const openPreview = useCallback((url: string | null) => {
    setZoom(1);
    setPreviewImage(url);
  }, []);

  useEffect(() => {
    if (!generatingPDF) { setPdfStatusIndex(0); return; }
    const interval = setInterval(() => setPdfStatusIndex(i => (i + 1) % pdfStatusMessages.length), 3000);
    return () => clearInterval(interval);
  }, [generatingPDF]);

  useEffect(() => {
    if (!downloadingImages) { setImageStatusIndex(0); return; }
    const interval = setInterval(() => setImageStatusIndex(i => (i + 1) % imageStatusMessages.length), 2500);
    return () => clearInterval(interval);
  }, [downloadingImages]);

  useEffect(() => {
    if (!isTemplateDropdownOpen) return;
    const onClick = (e: MouseEvent) => {
      if (templateDropdownRef.current && !templateDropdownRef.current.contains(e.target as Node))
        setIsTemplateDropdownOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [isTemplateDropdownOpen]);

  useEffect(() => {
    const el = zoomContainerRef.current;
    if (!el || !previewImage) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      setZoom((prev: number) => Math.min(Math.max(0.25, prev - e.deltaY * 0.001), 5));
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [previewImage]);

  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
        const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};

        const [suppliersRes, fabricTypesRes] = await Promise.all([
          fetch(`${baseUrl}/api/v1/suppliers`, { headers }),
          fetch(`${baseUrl}/api/v1/fabric-product-categories`, { headers })
        ]);

        const [suppliersJson, fabricTypesJson] = await Promise.all([
          suppliersRes.json(),
          fabricTypesRes.json()
        ]);

        if (suppliersJson.success) setSuppliers(suppliersJson.data);
        if (fabricTypesJson.success) setFabricTypes(fabricTypesJson.data);
      } catch (err) {
        console.error('Failed to fetch master data:', err);
      }
    };

    const fetchReport = async () => {
      try {
        const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
        const res = await fetch(`${baseUrl}/api/v1/fabric-inspections/${id}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const json = await res.json();
        if (json.success) {
          setReport(json.data);
        }
      } catch (err) {
        console.error('Failed to fetch fabric report detail:', err);
      } finally {
        setLoading(false);
      }
    };

    if (token && id) {
      fetchMasterData();
      fetchReport();
    }
  }, [id, token]);

  const getSupplierName = (id: string) => {
    const supplier = suppliers.find(s => s.id === id);
    return supplier ? supplier.name : id;
  };

  const getFabricTypeName = (id: string) => {
    const type = fabricTypes.find(t => t.id === id);
    return type ? type.title : id;
  };

  const handleDownload = () => {
    window.print();
  };

  const toDataUrl = async (url: string | null | undefined): Promise<string> => {
    if (!url) return '';
    if (url.startsWith('data:')) return url;
    const blobToDataUrl = (blob: Blob): Promise<string> =>
      new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => res(reader.result as string);
        reader.onerror = rej;
        reader.readAsDataURL(blob);
      });
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
    let abs = url;
    if (!/^(https?:|blob:)/.test(abs)) abs = `${baseUrl}${abs.startsWith('/') ? '' : '/'}${abs}`;
    const isGcs = abs.includes('storage.googleapis.com');
    const fetchUrl = isGcs ? `${baseUrl}/api/v1/proxy-image?url=${encodeURIComponent(abs)}` : abs;
    try {
      const res = await fetch(fetchUrl, { mode: 'cors' });
      if (res.ok) return await blobToDataUrl(await res.blob());
      console.warn('[PDF img] fetch failed', res.status, fetchUrl);
    } catch (e) {
      console.warn('[PDF img] fetch threw', e, fetchUrl);
    }
    console.error('[PDF img] could NOT convert, image will likely be missing:', url);
    return abs;
  };

  const handleGeneratePDF = async (template: PdfTemplate = 'qa-mate') => {
    if (!report) return;
    setGeneratingPDF(true);
    try {
      const clone: any = JSON.parse(JSON.stringify(report));

      const cache = new Map<string, string>();
      const resolve = async (url: string | null | undefined): Promise<string> => {
        if (!url) return '';
        if (cache.has(url)) return cache.get(url)!;
        const data = await toDataUrl(url);
        cache.set(url, data);
        return data;
      };
      const resolveArr = async (arr: string[] | undefined) => {
        if (!arr) return;
        for (let i = 0; i < arr.length; i++) if (arr[i]) arr[i] = await resolve(arr[i]);
      };

      // Checklist evidence
      for (const c of clone.checklists || []) await resolveArr(c.evidence_urls);
      // Defect images
      for (const d of clone.defects || []) await resolveArr(d.image_urls);
      // Documentation photos
      for (const p of clone.photos || []) { if (p?.image_url) p.image_url = await resolve(p.image_url); }
      // Dimension images
      await resolveArr(clone.roll_width_images);
      await resolveArr(clone.bow_skew_images);
      // Signatures
      if (clone.qa_signature_url) clone.qa_signature_url = await resolve(clone.qa_signature_url);
      if (clone.factory_signature_url) clone.factory_signature_url = await resolve(clone.factory_signature_url);

      // Inject resolved supplier/fabric names so PDF doesn't need lookup tables
      clone.supplier_name = getSupplierName(clone.supplier);
      clone.fabric_type_name = getFabricTypeName(clone.fabric_type);

      const docElement = template === 'migros'
        ? <MigrosFabricInspectionReportPDF report={clone} />
        : <FabricInspectionReportPDF report={clone} />;
      const filenamePrefix = template === 'migros' ? 'Migros-Fabric-Inspection' : 'Fabric-Inspection';
      const idPart = report.po_number || report.id.slice(0, 8);

      const blob = await pdf(docElement).toBlob();
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dlUrl;
      a.download = `${filenamePrefix}-${idPart}.pdf`;
      a.click();
      URL.revokeObjectURL(dlUrl);
    } catch (err: any) {
      console.error('PDF generation failed:', err);
      alert(`PDF generation failed: ${err?.message || 'Unknown error'}. Check browser console for details.`);
    } finally {
      setGeneratingPDF(false);
    }
  };

  const handleDownloadImages = async () => {
    if (!report) return;
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

    const fetchBlob = async (url: string): Promise<Blob | null> => {
      if (!url) return null;
      let abs = url;
      if (!/^(https?:|blob:|data:)/.test(abs)) abs = `${baseUrl}${abs.startsWith('/') ? '' : '/'}${abs}`;
      const isGcs = abs.includes('storage.googleapis.com');
      const fetchUrl = isGcs ? `${baseUrl}/api/v1/proxy-image?url=${encodeURIComponent(abs)}` : abs;
      try {
        const res = await fetch(fetchUrl, { mode: 'cors' });
        if (res.ok) return await res.blob();
      } catch {}
      return null;
    };

    const getExt = (url: string, blob: Blob) => {
      const m = url.match(/\.(\w{2,5})(\?|$)/);
      if (m) return m[1].toLowerCase();
      if (blob.type.includes('jpeg') || blob.type.includes('jpg')) return 'jpg';
      if (blob.type.includes('png')) return 'png';
      if (blob.type.includes('webp')) return 'webp';
      return 'jpg';
    };

    const docPhotos = (report.photos || []).map((p: any) => p?.image_url).filter(Boolean);
    const checklistEvidence = (report.checklists || []).flatMap((c: any) => c?.evidence_urls || c?.evidence_images || []).filter(Boolean);
    const defectImages = (report.defects || []).flatMap((d: any) => d?.image_urls || d?.evidence_images || []).filter(Boolean);
    const dimensionImages = [
      ...((report.roll_width_images as string[]) || []),
      ...((report.bow_skew_images as string[]) || []),
    ].filter(Boolean);

    const total = docPhotos.length + checklistEvidence.length + defectImages.length + dimensionImages.length;
    if (total === 0) { alert('No images found in this report.'); return; }

    setDownloadingImages(true);
    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();

      const docFolder = zip.folder('Documentation_Photos')!;
      for (let i = 0; i < docPhotos.length; i++) {
        const blob = await fetchBlob(docPhotos[i]);
        if (blob) docFolder.file(`${String(i + 1).padStart(2, '0')}_photo.${getExt(docPhotos[i], blob)}`, blob);
      }

      const checkFolder = zip.folder('Checklist_Evidence')!;
      for (let i = 0; i < checklistEvidence.length; i++) {
        const blob = await fetchBlob(checklistEvidence[i]);
        if (blob) checkFolder.file(`${String(i + 1).padStart(2, '0')}_evidence.${getExt(checklistEvidence[i], blob)}`, blob);
      }

      const defectFolder = zip.folder('Defect_Images')!;
      for (let i = 0; i < defectImages.length; i++) {
        const blob = await fetchBlob(defectImages[i]);
        if (blob) defectFolder.file(`${String(i + 1).padStart(2, '0')}_defect.${getExt(defectImages[i], blob)}`, blob);
      }

      const dimFolder = zip.folder('Dimension_Images')!;
      for (let i = 0; i < dimensionImages.length; i++) {
        const blob = await fetchBlob(dimensionImages[i]);
        if (blob) dimFolder.file(`${String(i + 1).padStart(2, '0')}_dimension.${getExt(dimensionImages[i], blob)}`, blob);
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(zipBlob);
      a.download = `Fabric-Inspection-Images-${report.id || 'report'}.zip`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err: any) {
      alert(`Image download failed: ${err?.message || 'Unknown error'}`);
    } finally {
      setDownloadingImages(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-slate-200 border-t-[#136bce]"></div>
        <p className="text-slate-500 font-medium">Loading report details...</p>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 text-center">
        <span className="material-symbols-outlined text-6xl text-slate-300">report_off</span>
        <h2 className="text-2xl font-bold text-slate-800">Report Not Found</h2>
        <p className="text-slate-500 max-w-md">The inspection report you are looking for could not be found or has been deleted.</p>
        <Link to="/inspection-report" className="mt-4 px-6 py-2 bg-[#136bce] text-white rounded-lg font-bold">Back to Reports</Link>
      </div>
    );
  }

  const isPass = report.result === 'PASS';

  const gsmSamples = report.gsm_samples || report.gsmSamples || [];
  let avgGsm = 0;
  if (gsmSamples.length > 0) {
    let total = 0;
    let count = 0;
    for (const s of gsmSamples) {
      const gsmVal = Number(s.original_gsm ?? s.originalGsm ?? 0);
      if (gsmVal > 0) {
        total += gsmVal;
        count++;
      }
    }
    if (count > 0) avgGsm = total / count;
  }
  const targetGsm = Number(report.target_gsm) || 0;
  const gsmVariation = targetGsm > 0 ? ((avgGsm - targetGsm) / targetGsm) * 100 : 0;

  return (
    <div className="mx-auto w-full max-w-full space-y-8 print:space-y-8 animate-in fade-in duration-500 pb-12 print:pb-0 print:animate-none">
      {/* Page Header */}
      <div className="flex flex-col items-start sm:flex-row sm:items-center justify-between gap-4 pb-2 print:hidden">
        <div>
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-1 font-medium">
            <Link to="/dashboard" className="hover:text-[#136bce]">Inspection</Link>
            <span className="material-symbols-outlined text-[14px]">chevron_right</span>
            <Link to="/inspection-report" className="hover:text-[#136bce]">Details</Link>
            <span className="material-symbols-outlined text-[14px]">chevron_right</span>
            <span className="text-[#136bce] font-semibold">Summary & Result</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Fabric Inspection Report</h1>
        </div>
        <div className="flex gap-3 w-full sm:w-auto">
          <button
            onClick={() => setPdfModalOpen(true)}
            disabled={generatingPDF}
            className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg shadow-md text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-4 focus:ring-emerald-500/30 transition-all active:scale-95 flex-1 sm:flex-none disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <span className="material-symbols-outlined text-[20px]">{generatingPDF ? 'hourglass_top' : 'picture_as_pdf'}</span>
            {generatingPDF ? 'Generating PDF…' : 'Generate PDF'}
          </button>
          <button
            onClick={handleDownloadImages}
            disabled={downloadingImages}
            className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg shadow-md text-sm font-bold text-white bg-violet-600 hover:bg-violet-700 focus:outline-none focus:ring-4 focus:ring-violet-500/30 transition-all active:scale-95 flex-1 sm:flex-none disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <span className="material-symbols-outlined text-[20px]">{downloadingImages ? 'hourglass_top' : 'photo_library'}</span>
            {downloadingImages ? 'Downloading…' : 'Download Images'}
          </button>
          <button
            onClick={handleDownload}
            className="inline-flex items-center justify-center px-6 py-2.5 rounded-lg shadow-md text-sm font-bold text-white bg-[#136bce] hover:bg-[#0e52a0] focus:outline-none focus:ring-4 focus:ring-blue-500/30 transition-all active:scale-95 flex-1 sm:flex-none"
          >
            <span className="material-symbols-outlined mr-2 text-[20px]">download</span>
            Download Report
          </button>
        </div>
      </div>

      {/* Standardized Result Status Banner */}
      <div className="bg-white rounded-2xl shadow-md border border-slate-200 overflow-hidden flex flex-col md:flex-row h-auto md:h-44">
        <div className={`md:w-1/3 text-white p-6 flex flex-col items-center justify-center text-center relative overflow-hidden ${isPass ? 'bg-[#10b981]' : 'bg-red-500'}`}>
          <div className={`absolute inset-0 bg-gradient-to-br opacity-100 z-0 ${isPass ? 'from-emerald-400 to-emerald-600' : 'from-red-400 to-red-600'}`}></div>
          <span className="material-symbols-outlined absolute -right-6 -bottom-6 text-[140px] opacity-20 rotate-12 select-none z-0">
            {isPass ? 'verified' : 'cancel'}
          </span>
          <div className="relative z-10 w-full flex flex-col items-center justify-center h-full">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md px-3 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase mb-2 shadow-sm ring-1 ring-white/30">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
              Inspection Complete
            </div>
            <h2 className="text-5xl font-black tracking-tighter drop-shadow-sm leading-none mb-1">{isPass ? 'PASS' : 'FAIL'}</h2>
            <p className="text-white/80 text-xs font-medium opacity-90">{isPass ? 'Meets acceptable quality limits (AQL)' : 'Does not meet acceptance criteria'}</p>
          </div>
        </div>
        <div className="md:w-2/3 p-6 flex items-center justify-around bg-white relative">
          <div className="text-center group">
            <p className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1">Total Penalty Points</p>
            <div className="flex items-baseline justify-center gap-2">
              <p className={`text-4xl font-black transition-colors ${(report.total_penalty_points || 0) > 40 ? 'text-red-500' : 'text-slate-800 group-hover:text-[#136bce]'}`}>{report.total_penalty_points || 0}</p>
              <span className="text-sm font-bold text-slate-400">pts</span>
            </div>
          </div>
          <div className="w-px h-20 bg-slate-100 mx-4"></div>
          <div className="text-center group">
            <p className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1">Defect Count</p>
            <div className="flex items-baseline justify-center gap-2">
              <p className="text-4xl font-black text-slate-800 group-hover:text-red-500 transition-colors">{report.defects ? report.defects.length : 0}</p>
              <span className="text-sm font-bold text-slate-400">found</span>
            </div>
          </div>
          <div className="w-px h-20 bg-slate-100 mx-4"></div>
          <div className="text-center group">
            <p className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1">Rolls Inspected</p>
            <div className="flex items-baseline justify-center gap-2">
              <p className="text-4xl font-black text-slate-800 group-hover:text-amber-500 transition-colors">{report.rolls_inspected || 0}</p>
              <span className="text-sm font-bold text-slate-400">units</span>
            </div>
          </div>
        </div>
      </div>

      {/* Basic Details Section */}
      <section className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/80 flex items-center gap-3">
          <div className="p-1.5 bg-blue-100 rounded-lg text-[#136bce]">
            <span className="material-symbols-outlined text-xl">info</span>
          </div>
          <h3 className="text-lg font-bold text-slate-800">Basic Details</h3>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-y-6 gap-x-8">
          {[
            { label: "Date of Inspection", value: formatReportDate(report.created_at) },
            { label: "Inspector Name", value: report.inspector_name },
            { label: "Factory/Supplier", value: getSupplierName(report.supplier) },
            { label: "Buyer/Client", value: report.buyer_name },
            { label: `${(report.inspection_basis || 'PO').toUpperCase()} Number`, value: report.po_number || 'N/A' },
            { label: "Style/Ref Number", value: report.style_number || 'N/A' },
            { label: "Fabric Type", value: getFabricTypeName(report.fabric_type) },
            { label: "Color Shade", value: report.color_shade || 'N/A' },
            { label: "Lot Size", value: `${report.lot_size || 0}` },
            { label: "Sample Size", value: `${report.sample_size || 0}` },
            { label: "Total Yards/Meters", value: `${report.total_yards || 0} yds` }
          ].map((item, i) => (
            <div key={i}>
              <p className="text-[10px] uppercase text-slate-500 font-bold mb-1 tracking-wide">{item.label}</p>
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-slate-900">{item.value || 'N/A'}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Inspection Details Section */}
      <section className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/80 flex items-center gap-3">
          <div className="p-1.5 bg-blue-100 rounded-lg text-[#136bce]">
            <span className="material-symbols-outlined text-xl">description</span>
          </div>
          <h3 className="text-lg font-bold text-slate-800">Inspection Details</h3>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-y-6 gap-x-8">
          {[
            { label: "Composition", value: Array.isArray(report.composition) ? report.composition.map((c: any) => `${c.percentage}% ${c.material}`).join(', ') : report.composition },
            { label: "Pantone", value: report.pantone?.code ? `${report.pantone.code} (${report.pantone.name})` : report.pantone },
            { label: "Selvedge", value: report.selvedge },
            { label: "Length", value: report.length },
            { label: "Width", value: report.width },
            { label: "Cuttable Width", value: (Number(report.width) > 0) ? (Number(report.width) - Number(report.selvedge || 0)).toFixed(2) : null },
            { label: "AQL Points", value: report.aql_points }
          ].map((item, i) => (
            <div key={i}>
              <p className="text-[10px] uppercase text-slate-500 font-bold mb-1 tracking-wide">{item.label}</p>
              <p className="text-base font-semibold text-slate-900">{item.value || 'N/A'}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Dimension Verification Section */}
      {((report.roll_width_samples && report.roll_width_samples.length > 0) ||
        (report.bow_skew_samples && report.bow_skew_samples.length > 0) ||
        (report.roll_width_images && report.roll_width_images.length > 0) ||
        (report.bow_skew_images && report.bow_skew_images.length > 0)) && (
        <section className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/80 flex items-center gap-3">
            <div className="p-1.5 bg-blue-100 rounded-lg text-[#136bce]">
              <span className="material-symbols-outlined text-xl">straighten</span>
            </div>
            <h3 className="text-lg font-bold text-slate-800">Dimension Verification</h3>
          </div>
          <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Roll Width */}
            <div>
              <h4 className="text-sm font-bold text-slate-700 mb-3 uppercase tracking-wider">Roll Width Samples</h4>
              {report.roll_width_samples && report.roll_width_samples.length > 0 ? (
                <div className="space-y-2">
                  {report.roll_width_samples.map((s, i) => (
                    <div key={i} className="flex items-center justify-between bg-slate-50 border border-slate-100 rounded-lg px-4 py-2">
                      <span className="text-xs font-bold text-slate-500">#{i + 1} • At {s.length_point != null ? `${s.length_point} m` : '-'}</span>
                      <span className="text-sm font-black text-slate-900">{s.value != null ? `${s.value} ${s.unit || 'in'}` : '-'}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-400 italic">No samples recorded.</p>
              )}
              {report.roll_width_images && report.roll_width_images.length > 0 && (
                <div className="mt-4">
                  <p className="text-[10px] uppercase font-bold text-slate-400 mb-2 tracking-wide">Images</p>
                  <div className="flex flex-wrap gap-3">
                    {report.roll_width_images.map((img, i) => (
                      <div
                        key={i}
                        className="w-20 h-20 overflow-hidden rounded-lg border-2 border-slate-100 hover:border-[#136bce] transition-colors cursor-zoom-in shadow-sm bg-slate-50"
                        onClick={() => openPreview(img)}
                      >
                        <img alt={`Roll width ${i}`} className="w-full h-full object-cover" src={img} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Bow & Skew */}
            <div>
              <h4 className="text-sm font-bold text-slate-700 mb-3 uppercase tracking-wider">Bow &amp; Skew Samples</h4>
              {report.bow_skew_samples && report.bow_skew_samples.length > 0 ? (
                <div className="space-y-2">
                  {report.bow_skew_samples.map((s, i) => (
                    <div key={i} className="flex items-center justify-between bg-slate-50 border border-slate-100 rounded-lg px-4 py-2">
                      <span className="text-xs font-bold text-slate-500">#{i + 1} • At {s.length_point != null ? `${s.length_point} m` : '-'}</span>
                      <span className="text-sm font-black text-slate-900">{s.value != null ? `${s.value} ${s.unit || 'in'}` : '-'}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-400 italic">No samples recorded.</p>
              )}
              {report.bow_skew_images && report.bow_skew_images.length > 0 && (
                <div className="mt-4">
                  <p className="text-[10px] uppercase font-bold text-slate-400 mb-2 tracking-wide">Images</p>
                  <div className="flex flex-wrap gap-3">
                    {report.bow_skew_images.map((img, i) => (
                      <div
                        key={i}
                        className="w-20 h-20 overflow-hidden rounded-lg border-2 border-slate-100 hover:border-[#136bce] transition-colors cursor-zoom-in shadow-sm bg-slate-50"
                        onClick={() => openPreview(img)}
                      >
                        <img alt={`Bow skew ${i}`} className="w-full h-full object-cover" src={img} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Weight Details Section */}
      <section className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/80 flex items-center gap-3">
          <div className="p-1.5 bg-orange-100 rounded-lg text-orange-600">
            <span className="material-symbols-outlined text-xl">scale</span>
          </div>
          <h3 className="text-lg font-bold text-slate-800">Weight Details</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <p className="text-[10px] uppercase text-slate-500 font-bold mb-1 tracking-wide">Target GSM</p>
              <p className="text-xl font-bold text-slate-900">{targetGsm > 0 ? targetGsm : '-'}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-slate-500 font-bold mb-1 tracking-wide">Average GSM</p>
              <p className="text-xl font-bold text-slate-900">{avgGsm > 0 ? avgGsm.toFixed(2) : '-'}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-slate-500 font-bold mb-1 tracking-wide">Variation</p>
              <p className={`text-xl font-bold ${Math.abs(gsmVariation) >= 5 ? 'text-red-600' : 'text-emerald-600'}`}>
                {avgGsm > 0 ? `${gsmVariation > 0 ? '+' : ''}${gsmVariation.toFixed(2)}%` : '-'}
              </p>
            </div>
          </div>
          
          {gsmSamples && gsmSamples.length > 0 && (
            <div>
              <h4 className="text-sm font-bold text-slate-700 mb-3 uppercase tracking-wider">Weight Samples</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {gsmSamples.map((s, i) => {
                  const sSize = s.sample_size ?? s.sampleSize;
                  const oGsm = Number(s.original_gsm ?? s.originalGsm ?? 0);
                  return (
                    <div key={i} className="flex items-center justify-between bg-slate-50 border border-slate-100 rounded-lg px-4 py-3">
                      <span className="text-xs font-bold text-slate-500">
                        Sample #{i + 1} {sSize ? `(${sSize})` : ''}
                      </span>
                      <span className="text-sm font-black text-slate-900">{oGsm > 0 ? `${oGsm} GSM` : '-'}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Ends & Picks Details Section */}
      <section className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/80 flex items-center gap-3">
          <div className="p-1.5 bg-purple-100 rounded-lg text-purple-600">
            <span className="material-symbols-outlined text-xl">grid_on</span>
          </div>
          <h3 className="text-lg font-bold text-slate-800">Ends &amp; Picks Details</h3>
        </div>
        <div className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-6">
          {(() => {
            const tEpi = Number(report.target_epi ?? (report as any).targetEpi ?? 0);
            const oEpi = Number(report.original_epi ?? (report as any).originalEpi ?? 0);
            const tPpi = Number(report.target_ppi ?? (report as any).targetPpi ?? 0);
            const oPpi = Number(report.original_ppi ?? (report as any).originalPpi ?? 0);
            
            const epiVar = tEpi > 0 ? ((oEpi - tEpi) / tEpi) * 100 : 0;
            const ppiVar = tPpi > 0 ? ((oPpi - tPpi) / tPpi) * 100 : 0;
            const epVariation = Math.abs(epiVar) > Math.abs(ppiVar) ? epiVar : ppiVar;
            
            return (
              <>
                <div>
                  <p className="text-[10px] uppercase text-slate-500 font-bold mb-1 tracking-wide">EPI (Target / Actual)</p>
                  <p className="text-xl font-bold text-slate-900">{tEpi} / {oEpi}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase text-slate-500 font-bold mb-1 tracking-wide">PPI (Target / Actual)</p>
                  <p className="text-xl font-bold text-slate-900">{tPpi} / {oPpi}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase text-slate-500 font-bold mb-1 tracking-wide">Variation</p>
                  <p className={`text-xl font-bold ${Math.abs(epVariation) >= 5 ? 'text-red-600' : 'text-emerald-600'}`}>
                    {(tEpi > 0 || tPpi > 0) ? `${epVariation > 0 ? '+' : ''}${epVariation.toFixed(2)}%` : '-'}
                  </p>
                </div>
              </>
            );
          })()}
        </div>
      </section>

      {/* Checklist Results Section */}
      <section className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/80 flex items-center gap-3">
          <div className="p-1.5 bg-blue-100 rounded-lg text-[#136bce]">
            <span className="material-symbols-outlined text-xl">playlist_add_check</span>
          </div>
          <h3 className="text-lg font-bold text-slate-800">Checklist Results</h3>
        </div>
        <div className="divide-y divide-slate-100">
          {(!report.checklists || report.checklists.length === 0) ? (
            <div className="p-12 text-center text-slate-400">No checklist records found.</div>
          ) : (
            report.checklists.map((item, idx) => (
              <div key={idx} className="p-6 grid grid-cols-1 lg:grid-cols-12 gap-8">
                <div className="lg:col-span-4">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="text-base font-bold text-slate-900">{item.title}</h4>
                    <span className={`px-2.5 py-0.5 rounded-md text-[10px] font-bold border uppercase tracking-wider ${
                      item.status === 'pass' ? 'bg-emerald-50 text-emerald-800 border-emerald-100' : 'bg-red-50 text-red-800 border-red-100'
                    }`}>
                      {item.status}
                    </span>
                  </div>
                  <p className="text-sm text-slate-500 leading-relaxed">{item.remarks}</p>
                </div>
                <div className="lg:col-span-8">
                  <p className="text-[10px] font-bold text-slate-400 mb-2 uppercase tracking-wider">Evidence Photos</p>
                  <div className="flex flex-wrap gap-3">
                    {item.evidence_urls && item.evidence_urls.map((img, i) => (
                      <div 
                        key={i} 
                        className="w-20 h-20 overflow-hidden rounded-lg border-2 border-slate-100 hover:border-[#136bce] transition-colors cursor-zoom-in shadow-sm bg-slate-50"
                        onClick={() => openPreview(img)}
                      >
                        <img alt={`Evidence ${i}`} className="w-full h-full object-cover" src={img} />
                      </div>
                    ))}
                    {(!item.evidence_urls || item.evidence_urls.length === 0) && <p className="text-xs text-slate-400 mt-2 italic">No images attached.</p>}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* Defect Log Section */}
      <section className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/80 flex items-center gap-3">
          <div className="p-1.5 bg-blue-100 rounded-lg text-[#136bce]">
            <span className="material-symbols-outlined text-xl">bug_report</span>
          </div>
          <h3 className="text-lg font-bold text-slate-800">Defect Log</h3>
        </div>
        <div className="p-6 space-y-8">
          {(!report.defects || report.defects.length === 0) ? (
            <div className="p-12 text-center text-slate-400">No defects logged for this inspection.</div>
          ) : (
            report.defects.map((log, idx) => (
              <div key={idx} className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                <div className="bg-slate-100 px-6 py-4 border-b border-slate-200 flex flex-col sm:flex-row justify-between sm:items-center gap-2">
                  <div className="flex items-center gap-3">
                    <span className="material-symbols-outlined text-slate-500">inbox</span>
                    <h4 className="text-base font-extrabold text-slate-800 tracking-tight">Roll Number: {log.roll_number}</h4>
                  </div>
                  <div className="text-sm font-medium text-slate-500 flex items-center gap-4">
                    <span className="flex items-center gap-1"><span className="material-symbols-outlined text-base">gavel</span> Penalty Points: {log.points}</span>
                  </div>
                </div>
                <div className="p-6 bg-white hover:bg-slate-50 transition-colors">
                  <div className="flex flex-col lg:flex-row gap-8">
                    <div className="lg:w-1/3 shrink-0">
                      <p className="text-[10px] uppercase font-bold text-slate-400 mb-3 tracking-wide flex items-center gap-1">
                        <span className="material-symbols-outlined text-sm">photo_library</span> Defect Images
                      </p>
                      <div className="flex gap-4 overflow-x-auto pb-2 custom-scrollbar">
                        {log.image_urls && log.image_urls.map((img, i) => (
                          <div 
                            key={i} 
                            className="h-40 w-40 shrink-0 object-cover rounded-xl border border-slate-200 shadow-sm cursor-zoom-in hover:ring-2 ring-[#136bce] transition-all bg-white overflow-hidden"
                            onClick={() => openPreview(img)}
                          >
                            <img alt="Defect" className="w-full h-full object-cover" src={img} />
                          </div>
                        ))}
                        {(!log.image_urls || log.image_urls.length === 0) && <p className="text-xs text-slate-400">No images.</p>}
                      </div>
                    </div>
                    <div className="lg:w-2/3 flex flex-col justify-between gap-6">
                      <div>
                        <div className="flex flex-col sm:flex-row justify-between items-start mb-6 gap-4">
                          <div>
                            <div className="flex items-center gap-3 mb-2">
                              <h5 className="text-2xl font-bold text-slate-900 tracking-tight">{log.type}</h5>
                            </div>
                            {(log.category || log.defect_type) && (
                              <div className="flex flex-wrap gap-2">
                                {log.category && (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-red-50 border border-red-200 text-[10px] font-black uppercase tracking-wide text-red-600">
                                    Category: {log.category}
                                  </span>
                                )}
                                {log.defect_type && (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-red-50 border border-red-200 text-[10px] font-black uppercase tracking-wide text-red-600">
                                    Type: {log.defect_type}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex gap-8 text-right bg-slate-50 p-3 rounded-lg border border-slate-100 shadow-sm">
                            <div>
                              <span className="block text-xs uppercase text-slate-400 font-bold mb-1">Yardage</span>
                              <span className="block text-3xl font-black text-slate-800 tracking-tight">{log.yard_number} <span className="text-sm font-medium text-slate-400">yds</span></span>
                            </div>
                            <div className="w-px bg-slate-200"></div>
                            <div>
                              <span className="block text-xs uppercase text-slate-400 font-bold mb-1">Penalty</span>
                              <div className="flex items-center gap-1 justify-end">
                                <span className="block text-3xl font-black text-red-500 tracking-tight">{log.points}</span>
                                <span className="text-sm font-bold text-red-500 uppercase">pts</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase font-bold text-slate-400 mb-2 tracking-wide flex items-center gap-1">
                            <span className="material-symbols-outlined text-sm">notes</span> Description
                          </p>
                          <p className="text-base text-slate-600 leading-relaxed bg-slate-50 p-5 rounded-xl border border-slate-100 shadow-sm">
                            {log.description || 'No description provided.'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* Documentation Photos Section */}
      <section className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/80 flex items-center gap-3">
          <div className="p-1.5 bg-blue-100 rounded-lg text-[#136bce]">
            <span className="material-symbols-outlined text-xl">photo_camera</span>
          </div>
          <h3 className="text-lg font-bold text-slate-800">Documentation Photos</h3>
        </div>
        <div className="p-6">
          {(!report.photos || report.photos.length === 0) ? (
            <div className="p-12 text-center text-slate-400">No documentation photos captured.</div>
          ) : (
            <div className="flex flex-wrap gap-4">
              {report.photos.map((photo, idx) => (
                <div key={photo.id || idx} className="flex flex-col items-center gap-1.5">
                  <div
                    className={`w-16 h-16 overflow-hidden rounded-lg border-2 ${photo.image_url ? 'border-slate-100 hover:border-[#136bce] cursor-zoom-in' : 'border-dashed border-slate-200'} transition-colors shadow-sm bg-slate-50`}
                    onClick={() => photo.image_url && openPreview(photo.image_url)}
                  >
                    {photo.image_url ? (
                      <img alt={photo.label} className="w-full h-full object-cover" src={photo.image_url} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="material-symbols-outlined text-xl text-slate-300">no_photography</span>
                      </div>
                    )}
                  </div>
                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider text-center max-w-[72px] leading-tight">{photo.label}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* 4 Point Inspection Summary Section */}
      <section className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/80 flex items-center gap-3">
          <div className="p-1.5 bg-blue-100 rounded-lg text-[#136bce]">
            <span className="material-symbols-outlined text-xl">analytics</span>
          </div>
          <h3 className="text-lg font-bold text-slate-800">4 Point Inspection Summary</h3>
        </div>
        <div className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { label: "Total Yards Inspected", value: report.total_yards, unit: "yds", color: "text-slate-900" },
              { label: "Total Penalty Points", value: report.total_penalty_points, unit: "pts", color: "text-[#136bce]" },
              { label: "Average Points per 100 Sq. Yds", value: ((report.total_penalty_points * 3600) / (report.total_yards * (report.width || 60))).toFixed(1), unit: "pts", color: "text-slate-900" },
              { label: "Acceptance Standard", sub: "Max 40 points / 100 sq. yds", progress: Math.min(100, (report.total_penalty_points / 40) * 100) }
            ].map((item, i) => (
              <div key={i} className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                <p className="text-[10px] uppercase text-slate-500 font-bold mb-2 tracking-wide">{item.label}</p>
                {item.value !== undefined ? (
                  <p className={`text-2xl font-black ${item.color}`}>{item.value} <span className="text-sm font-semibold text-slate-400">{item.unit}</span></p>
                ) : (
                  <>
                    <p className="text-sm font-bold text-slate-700">{item.sub}</p>
                    <div className="w-full bg-slate-200 rounded-full h-1.5 mt-3">
                      <div className={`h-1.5 rounded-full ${item.progress > 100 ? 'bg-red-500' : 'bg-emerald-500'}`} style={{ width: `${item.progress}%` }}></div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Inspector's Remarks & Authorization */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          <h3 className="text-lg font-bold text-slate-800 mb-4 tracking-tight">Inspector's Remarks</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-[10px] uppercase font-bold text-slate-500 mb-2 tracking-wide">Overall Assessment</label>
              <div className="p-4 bg-slate-50 rounded-xl text-slate-800 text-sm leading-relaxed border border-slate-200 shadow-sm">
                {report.overall_remarks || 'No remarks provided.'}
              </div>
            </div>

          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col justify-between">
          <div>
            <h3 className="text-lg font-bold text-slate-800 mb-4 tracking-tight">Authorization</h3>
            <div className="grid grid-cols-2 gap-6 mb-6">
              <div>
                <p className="text-[10px] uppercase text-slate-500 font-bold mb-1">Inspector Name</p>
                <p className="text-lg font-bold text-slate-900 tracking-tight">{report.inspector_name}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase text-slate-500 font-bold mb-1">Date Signed</p>
                <span className="text-sm font-bold text-slate-900">{formatReportDate(report.created_at)}</span>
              </div>
            </div>
          </div>
          <div className="mt-auto">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-[10px] uppercase text-slate-500 font-bold mb-2">Factory Rep Signature</p>
                <div className="h-28 bg-slate-50 rounded-xl border-2 border-dashed border-slate-300 flex items-center justify-center mb-3 group hover:border-[#136bce] transition-colors overflow-hidden">
                  {report.factory_signature_url ? (
                    <img alt="Factory Rep Signature" className="h-16 opacity-80 group-hover:opacity-100 transition-opacity" src={report.factory_signature_url} />
                  ) : (
                    <span className="text-slate-400 text-xs italic">No signature captured</span>
                  )}
                </div>
                <div className="flex items-center text-slate-400 text-[10px] font-medium">
                  <span className="material-symbols-outlined text-xs mr-1 text-[#136bce]" style={{ fontVariationSettings: "'FILL' 1" } as React.CSSProperties}>verified_user</span>
                  Digitally verified by QA Mate Secure Auth.
                </div>
              </div>
              <div>
                <p className="text-[10px] uppercase text-slate-500 font-bold mb-2">QA Inspector Signature</p>
                <div className="h-28 bg-slate-50 rounded-xl border-2 border-dashed border-slate-300 flex items-center justify-center mb-3 group hover:border-[#136bce] transition-colors overflow-hidden">
                  {report.qa_signature_url ? (
                    <img alt="QA Inspector Signature" className="h-16 opacity-80 group-hover:opacity-100 transition-opacity" src={report.qa_signature_url} />
                  ) : (
                    <span className="text-slate-400 text-xs italic">No signature captured</span>
                  )}
                </div>
                <div className="flex items-center text-slate-400 text-[10px] font-medium">
                  <span className="material-symbols-outlined text-xs mr-1 text-[#136bce]" style={{ fontVariationSettings: "'FILL' 1" } as React.CSSProperties}>verified_user</span>
                  Digitally verified by QA Mate Secure Auth.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reference Guide */}
      <section className="bg-blue-50/50 rounded-2xl border border-blue-100 p-8 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2 text-[#136bce]">
              <span className="material-symbols-outlined text-2xl">menu_book</span>
              <h3 className="font-bold text-base text-slate-900 uppercase tracking-wider">Reference Guide</h3>
            </div>
            <h4 className="font-extrabold text-slate-900 text-3xl">The 4-Point System</h4>
          </div>
          <p className="text-slate-600 text-sm max-w-lg leading-relaxed font-medium md:text-right">
            Use this reference to assign penalty points correctly during inspection based on defect length.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { pts: "1", label: "1 Point", sub: "Defects up to 3 inches" },
            { pts: "2", label: "2 Points", sub: "Defects > 3\" and ≤ 6\"" },
            { pts: "3", label: "3 Points", sub: "Defects > 6\" and ≤ 9\"" },
            { pts: "4", label: "4 Points", sub: "Defects over 9 inches" }
          ].map((rule, i) => (
            <div key={i} className="flex items-center gap-4 p-5 bg-white rounded-xl shadow-sm border border-slate-100 transition-all hover:scale-[1.02]">
              <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-extrabold text-xl shrink-0">{rule.pts}</div>
              <div>
                <p className="font-bold text-slate-900 text-lg leading-tight">{rule.label}</p>
                <p className="text-xs text-slate-500 font-medium mt-0.5 tracking-tight">{rule.sub}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="pt-6 border-t border-blue-200">
          <h5 className="font-bold text-slate-900 mb-4 flex items-center gap-2 text-sm uppercase tracking-wide">
            <span className="material-symbols-outlined text-amber-500 text-lg" style={{ fontVariationSettings: "'FILL' 1" } as React.CSSProperties}>warning</span>
            Important Rules
          </h5>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              "Max 4 points per linear yard/meter regardless of defect number.",
              "Holes ≤ 1\" (2.5cm) = 2 points.",
              "Holes > 1\" (2.5cm) = 4 points.",
              "Continuous defects count as 4 points for every yard/meter."
            ].map((rule, i) => (
              <div key={i} className="flex items-start gap-2 text-sm text-slate-700 font-medium leading-relaxed">
                <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-1.5 shrink-0"></span>
                <span>{rule}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PDF Generation Modal — premium animated */}
      {generatingPDF && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/70 backdrop-blur-md p-4 animate-in fade-in duration-300 print:hidden">
          <div className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 animate-in zoom-in-95 slide-in-from-bottom-4 duration-500">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-400 via-blue-500 to-emerald-400 bg-[length:200%_100%] animate-gradient-x" />
            <div className="pointer-events-none absolute -top-16 -right-16 h-48 w-48 rounded-full bg-emerald-300/30 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-16 -left-16 h-48 w-48 rounded-full bg-blue-300/30 blur-3xl" />
            <div className="relative p-8 flex flex-col items-center text-center">
              <div className="relative mb-6">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-emerald-400 to-blue-500 blur-xl opacity-50 animate-pulse" />
                <div className="relative h-20 w-20 rounded-2xl bg-gradient-to-br from-emerald-500 to-blue-600 flex items-center justify-center shadow-lg shadow-emerald-500/40">
                  <span className="material-symbols-outlined text-white text-[40px] animate-pulse">picture_as_pdf</span>
                </div>
                <div className="absolute inset-0 animate-spin" style={{ animationDuration: '3s' }}>
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 h-3 w-3 rounded-full bg-emerald-500 shadow-md shadow-emerald-500/50" />
                </div>
              </div>
              <h2 className="text-2xl font-black text-slate-900 tracking-tight mb-2">Generating your PDF</h2>
              <p className="text-sm text-slate-500 font-medium leading-relaxed mb-6 max-w-xs">
                We're crafting a beautifully formatted fabric inspection report. This usually takes just a few seconds.
              </p>
              <div className="w-full bg-slate-50 rounded-xl border border-slate-100 px-4 py-3 mb-6 overflow-hidden">
                <div className="flex items-center gap-3">
                  <div className="relative flex h-2 w-2 shrink-0">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                  </div>
                  <p key={pdfStatusIndex} className="text-sm font-bold text-slate-700 text-left flex-1 animate-in fade-in slide-in-from-bottom-1 duration-500">
                    {pdfStatusMessages[pdfStatusIndex]}
                  </p>
                </div>
              </div>
              <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full w-1/3 bg-gradient-to-r from-emerald-500 to-blue-500 rounded-full animate-pdf-progress" />
              </div>
              <p className="mt-6 text-[10px] uppercase tracking-widest font-bold text-slate-400">
                Powered by <span className="text-emerald-600">QA Mate</span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Image Download Modal — premium animated */}
      {downloadingImages && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/70 backdrop-blur-md p-4 animate-in fade-in duration-300 print:hidden">
          <div className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 animate-in zoom-in-95 slide-in-from-bottom-4 duration-500">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-violet-400 via-fuchsia-500 to-violet-400 bg-[length:200%_100%] animate-gradient-x" />
            <div className="pointer-events-none absolute -top-16 -right-16 h-48 w-48 rounded-full bg-violet-300/30 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-16 -left-16 h-48 w-48 rounded-full bg-fuchsia-300/30 blur-3xl" />
            <div className="relative p-8 flex flex-col items-center text-center">
              <div className="relative mb-6">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-violet-400 to-fuchsia-500 blur-xl opacity-50 animate-pulse" />
                <div className="relative h-20 w-20 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center shadow-lg shadow-violet-500/40">
                  <span className="material-symbols-outlined text-white text-[40px] animate-pulse">photo_library</span>
                </div>
                <div className="absolute inset-0 animate-spin" style={{ animationDuration: '3s' }}>
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 h-3 w-3 rounded-full bg-violet-500 shadow-md shadow-violet-500/50" />
                </div>
              </div>
              <h2 className="text-2xl font-black text-slate-900 tracking-tight mb-2">Downloading Images</h2>
              <p className="text-sm text-slate-500 font-medium leading-relaxed mb-6 max-w-xs">
                Fetching and packaging all report images into a neatly organised ZIP folder.
              </p>
              <div className="w-full bg-slate-50 rounded-xl border border-slate-100 px-4 py-3 mb-6 overflow-hidden">
                <div className="flex items-center gap-3">
                  <div className="relative flex h-2 w-2 shrink-0">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-violet-400 opacity-75 animate-ping" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-violet-500" />
                  </div>
                  <p key={imageStatusIndex} className="text-sm font-bold text-slate-700 text-left flex-1 animate-in fade-in slide-in-from-bottom-1 duration-500">
                    {imageStatusMessages[imageStatusIndex]}
                  </p>
                </div>
              </div>
              <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full w-1/3 bg-gradient-to-r from-violet-500 to-fuchsia-500 rounded-full animate-pdf-progress" />
              </div>
              <p className="mt-6 text-[10px] uppercase tracking-widest font-bold text-slate-400">
                Powered by <span className="text-violet-600">QA Mate</span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ───── PDF Template Selector Modal ───── */}
      {pdfModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4 print:hidden"
          onClick={() => !generatingPDF && setPdfModalOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md overflow-hidden animate-in zoom-in-95 fade-in duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-emerald-600 text-[22px]">picture_as_pdf</span>
                <h3 className="text-base font-bold text-slate-900">Generate PDF Report</h3>
              </div>
              <button onClick={() => setPdfModalOpen(false)} disabled={generatingPDF} className="text-slate-400 hover:text-slate-600 disabled:opacity-30">
                <span className="material-symbols-outlined text-[22px]">close</span>
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Template</label>
                <div className="relative" ref={templateDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setIsTemplateDropdownOpen(prev => !prev)}
                    disabled={generatingPDF}
                    className="w-full flex items-center gap-2 rounded-lg border border-slate-200 bg-white py-2.5 pl-3 pr-9 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors outline-none cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <span className={`h-6 w-6 rounded-full ${selectedTemplateOption.iconBg} flex items-center justify-center shrink-0`}>
                      <span className={`material-symbols-outlined text-[14px] ${selectedTemplateOption.iconColor}`}>{selectedTemplateOption.icon}</span>
                    </span>
                    <span className="truncate">{selectedTemplateOption.name}</span>
                    <span className="material-symbols-outlined pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[18px] text-slate-400">expand_more</span>
                  </button>
                  {isTemplateDropdownOpen && (
                    <div className="absolute left-0 right-0 top-full mt-1 z-20 rounded-xl border border-slate-200 bg-white shadow-lg py-1 overflow-hidden">
                      {visibleTemplateOptions.map(opt => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => { setSelectedTemplate(opt.value); setIsTemplateDropdownOpen(false); }}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-blue-50 transition-colors text-left ${selectedTemplate === opt.value ? 'bg-blue-50 text-[#136bce] font-semibold' : 'text-slate-700'}`}
                        >
                          <span className={`h-6 w-6 rounded-full ${opt.iconBg} flex items-center justify-center shrink-0`}>
                            <span className={`material-symbols-outlined text-[14px] ${opt.iconColor}`}>{opt.icon}</span>
                          </span>
                          {opt.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-[11px] text-slate-500 mt-2 leading-snug">{selectedTemplateOption.description}</p>
              </div>
              {generatingPDF && (
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 flex items-center gap-2">
                  <span className="material-symbols-outlined text-emerald-600 text-[18px] animate-spin">progress_activity</span>
                  <span className="text-xs font-medium text-emerald-800">{pdfStatusMessages[pdfStatusIndex]}</span>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-end gap-2">
              <button onClick={() => setPdfModalOpen(false)} disabled={generatingPDF} className="rounded-lg px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-200 transition-colors disabled:opacity-40">
                Cancel
              </button>
              <button
                onClick={async () => { await handleGeneratePDF(selectedTemplate); setPdfModalOpen(false); }}
                disabled={generatingPDF}
                className="flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-2 text-sm font-bold text-white shadow-md shadow-emerald-500/30 hover:bg-emerald-700 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span className="material-symbols-outlined text-[18px]">{generatingPDF ? 'hourglass_top' : 'picture_as_pdf'}</span>
                {generatingPDF ? 'Generating…' : 'Generate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {previewImage && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/90 backdrop-blur-sm p-4 animate-in fade-in duration-200 print:hidden"
          onClick={() => { setPreviewImage(null); setZoom(1); }}
        >
          {/* Close */}
          <button 
            className="absolute top-6 right-6 text-white hover:text-slate-300 transition-colors p-2 rounded-full bg-slate-800/50 z-10"
            onClick={(e) => { e.stopPropagation(); setPreviewImage(null); setZoom(1); }}
          >
            <span className="material-symbols-outlined text-[32px]">close</span>
          </button>

          {/* Zoom controls */}
          <div 
            className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3 bg-slate-900/80 backdrop-blur-md border border-white/10 rounded-full px-4 py-2 z-10"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              className="text-white/70 hover:text-white transition-colors disabled:opacity-30"
              onClick={() => setZoom((prev: number) => Math.max(0.25, parseFloat((prev - 0.25).toFixed(2))))}
              disabled={zoom <= 0.25}
            >
              <span className="material-symbols-outlined text-[22px]">remove</span>
            </button>
            <span className="text-white text-xs font-bold tracking-widest w-12 text-center select-none">
              {Math.round(zoom * 100)}%
            </span>
            <button 
              className="text-white/70 hover:text-white transition-colors disabled:opacity-30"
              onClick={() => setZoom((prev: number) => Math.min(5, parseFloat((prev + 0.25).toFixed(2))))}
              disabled={zoom >= 5}
            >
              <span className="material-symbols-outlined text-[22px]">add</span>
            </button>
            <div className="w-px h-4 bg-white/20 mx-1" />
            <button 
              className="text-white/70 hover:text-white transition-colors"
              onClick={() => setZoom(1)}
            >
              <span className="material-symbols-outlined text-[20px]">fit_screen</span>
            </button>
          </div>

          {/* Image container — wheel listener attached via ref */}
          <div 
            ref={zoomContainerRef}
            className="flex items-center justify-center overflow-hidden max-h-[90vh] max-w-[90vw]"
            style={{ cursor: zoom > 1 ? 'grab' : 'zoom-in' }}
            onClick={(e) => e.stopPropagation()}
          >
            <img 
              src={previewImage} 
              alt="Preview" 
              className="rounded-lg shadow-2xl animate-in zoom-in-95 duration-300 max-h-[82vh] max-w-[88vw] object-contain" 
              style={{ transform: `scale(${zoom})`, transformOrigin: 'center', transition: 'transform 0.1s ease' }}
              draggable={false}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default FabricInspectionReportDetail;
